import {useCallback, useMemo, useState} from 'react';
import {IconButton, InputAdornment, TextFieldProps} from '@mui/material';
import {
  GridFilterModel,
  gridFilterModelSelector,
  GridRenderHeaderFilterProps,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import {ReactComponent as ClearIcon} from '../../../../../assets/images/filter-clear-icon.svg';

import './FilterTextField.scss';
import ArrTextField from '../../../arr-text-field/ArrTextField';
type FilterTextFieldProps = {
  gridRenderHeaderFilterProps: GridRenderHeaderFilterProps;
  operator?: 'contains' | 'equal' | 'startWith' | 'endWith';
  className?: string;
  dataTestId: string;
} & Omit<TextFieldProps, 'variant' | 'type'>;

const FilterTextField = ({
  gridRenderHeaderFilterProps,
  operator = 'contains',
  className,
  dataTestId,
  ...rest
}: FilterTextFieldProps) => {
  const {colDef} = gridRenderHeaderFilterProps;
  const apiRef = useGridApiContext();
  const {inputRef} = gridRenderHeaderFilterProps;
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  // Find the current filters applied to this column
  const currentFieldFilters = useMemo(
    () => filterModel.items?.filter(({field}) => field === colDef.field),
    [colDef.field, filterModel.items]
  );

  const value = currentFieldFilters[0]?.value ?? '';
  const [filterValue, setFilterValue] = useState(value);

  const handleTextUpdate = (value: string) => {
    const filterModel: GridFilterModel =
      apiRef.current.state.filter.filterModel;

    // If there's no value, delete the filter for this column
    if (!value) {
      if (currentFieldFilters[0]) {
        apiRef.current.deleteFilterItem(currentFieldFilters[0]);
      }
      return;
    }

    // Otherwise, upsert a new filter or update an existing one
    const updatedFilterModal: GridFilterModel = {
      items: [
        {
          ...(currentFieldFilters[0] || {
            field: colDef.field,
            operatorValue: operator ?? 'contains', // runtime property
          }),
          value: value,
        },
        ...filterModel.items.filter((item) => item.field !== colDef.field),
      ],
    };
    apiRef.current?.setFilterModel(updatedFilterModal);
  };

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilterValue(event.target.value);
    },
    [apiRef, colDef.field, currentFieldFilters]
  );

  const handleClear = useCallback(
    (event: any) => {
      event.preventDefault();
      setFilterValue('');
      apiRef.current.deleteFilterItem(currentFieldFilters[0]);
      // focus the input after clearing so keyboard users can continue typing
      // try the provided inputRef first; then fallback to a nearby input in the same wrapper
      requestAnimationFrame(() => {
        const el = (inputRef?.current as HTMLInputElement | null) ?? null;
        if (el) {
          el.focus();
          return;
        }
        const wrapper = (event?.currentTarget as HTMLElement)?.closest?.(
          '.filter-text-field'
        ) as HTMLElement | null;
        const fallback = wrapper?.querySelector(
          'input'
        ) as HTMLInputElement | null;
        fallback?.focus();
      });
    },
    [apiRef, colDef.field, currentFieldFilters, inputRef]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTextUpdate(filterValue);
    }
  };

  return (
    <ArrTextField
      className={`filter-text-field ${className}`}
      inputRef={inputRef}
      value={filterValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      state="enabled"
      sizeVariant="small"
      dataTestId={`filter-value-${dataTestId}`}
      slotProps={{
        inputLabel: {shrink: false},
        input: {
          endAdornment: filterValue && (
            <InputAdornment position="end">
              <IconButton
                data-testid="grid-header-filter-clear"
                onClick={(e) => handleClear(e)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === 'Enter' || e.code === 'Space') {
                    handleClear(e);
                  }
                }}
                size="small"
                tabIndex={0}
              >
                <ClearIcon className="clear-icon" />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
      {...rest}
    />
  );
};

export default FilterTextField;
